import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./contacts-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Button from "../../ui/button";
import Table from "../../ui/table";
import { 
  getIndependentContacts, 
  searchIndependentContacts,
  createIndependentContact, 
  updateIndependentContact, 
  deleteIndependentContact 
} from "../../../services/apiService";
import { showSuccessToast, showErrorToast, notifications } from "../../../utils/notifications";

const ContactsModal = ({ isOpen, onClose, initialData }) => {
  // ALL REFS MUST BE DECLARED FIRST
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);
  const debouncedSearchRef = useRef(null);
  
  // ALL STATE HOOKS
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: initialData?.phoneNumber || "",
  });
  const [contacts, setContacts] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // ALL CALLBACK HOOKS
  const performSearch = useCallback(async (value) => {
    if (value.trim() === "") {
      try {
        setIsSearching(true);
        const contactsData = await getIndependentContacts();
        setContacts(contactsData || []);
      } catch (error) {
        console.error("Error fetching contacts:", error);
        showErrorToast("Error al cargar los contactos");
      } finally {
        setIsSearching(false);
      }
      return;
    }

    setIsSearching(true);
    try {
      const searchResults = await searchIndependentContacts(value);
      setContacts(searchResults || []);
    } catch (error) {
      console.error("Error searching contacts:", error);
      showErrorToast("Error al buscar contactos");
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    
    debouncedSearchRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  }, [performSearch]);

  // ALL EFFECT HOOKS
  useEffect(() => {
    // Actualizar teléfono si se proporciona initialData
    if (initialData?.phoneNumber) {
      setFormData(prev => ({
        ...prev,
        telefono: initialData.phoneNumber
      }));
    }
  }, [initialData]);

  useEffect(() => {
    const fetchContacts = async () => {
      if (!isOpen) return;
      
      setIsLoading(true);
      try {
        const contactsData = await getIndependentContacts();
        setContacts(contactsData || []);
      } catch (error) {
        console.error("Error fetching contacts:", error);
        showErrorToast("Error al cargar los contactos");
      } finally {
        setIsLoading(false);
      }
    };

    fetchContacts();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, [isOpen, onClose]);

  // EARLY RETURN AFTER ALL HOOKS
  if (!isOpen) return null;

  // REGULAR FUNCTIONS AND HANDLERS
  const isFormValid = () => {
    return formData.nombre.trim() !== "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSaveContact = async () => {
    if (!isFormValid()) {
      showErrorToast("El nombre es obligatorio");
      return;
    }

    try {
      const contactData = {
        nombre: formData.nombre.trim(),
        telefono: formData.telefono?.trim() || null
      };

      if (isEditMode) {
        const updatedContact = await updateIndependentContact(editingContactId, contactData);
        setContacts(contacts.map(c => c.id === editingContactId ? updatedContact : c));
        showSuccessToast(notifications.update.success("Contacto"));
      } else {
        const newContact = await createIndependentContact(contactData);
        setContacts([...contacts, newContact]);
        showSuccessToast(notifications.create.success("Contacto"));
      }
      
      handleCancelEdit();
      
    } catch (error) {
      console.error("Error saving contact:", error);
      if (error.response?.status === 400) {
        showErrorToast("No se puede modificar este contacto porque tiene relaciones asociadas");
      } else if (error.response?.status === 404) {
        showErrorToast("El contacto no existe");
      } else {
        showErrorToast(
          isEditMode 
            ? notifications.update.error("el contacto")
            : notifications.create.error("el contacto")
        );
      }
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el contacto "${contact.nombre}"?`)) {
      return;
    }

    try {
      await deleteIndependentContact(contact.id);
      setContacts(contacts.filter(c => c.id !== contact.id));
      showSuccessToast(notifications.delete.success("Contacto"));
    } catch (error) {
      console.error("Error deleting contact:", error);
      if (error.response?.status === 400) {
        showErrorToast("No se puede eliminar este contacto porque tiene relaciones asociadas");
      } else if (error.response?.status === 404) {
        showErrorToast("El contacto no existe o ya fue eliminado");
      } else {
        showErrorToast(notifications.delete.error("el contacto"));
      }
    }
  };

  const handleContactRowClick = (contact) => {
    setFormData({
      nombre: contact.nombre || "",
      telefono: contact.telefono || "",
    });
    
    setIsEditMode(true);
    setEditingContactId(contact.id);
  };

  const handleCancelEdit = () => {
    setFormData({
      nombre: "",
      telefono: "",
    });
    setIsEditMode(false);
    setEditingContactId(null);
  };

  const headers = [
    { label: "Nombre", key: "nombre" },
    { 
      label: "Teléfono", 
      key: "telefono", 
      render: (value) => value || "No especificado" 
    },
  ];

  const renderActions = (row) => {
    return (
      <div style={{ display: "flex", justifyContent: "center", gap: "8px", width: "100%" }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteContact(row);
          }}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Eliminar contacto"
        >
          <MyIcons name="trash" width={20} height={20} color="#dc3545" />
        </button>
      </div>
    );
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Contactos</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons name="close" width={24} height={24} color="black" />
          </button>
        </div>

        <div className={styles.formSection}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              width: "100%",
            }}
          >
            <Input
              label="Nombre"
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              style={{ flex: 1 }}
              placeholder="Nombre del contacto (obligatorio)"
            />
            <Input
              label="Teléfono"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              style={{ flex: 1 }}
              placeholder="Número de teléfono (opcional)"
            />
          </div>
        </div>
        
        <div style={{ marginBottom: "16px", display: "flex", gap: "16px" }}>
          {!isEditMode ? (
            <Button 
              type="button" 
              variant="secondary" 
              onClick={handleSaveContact}
              disabled={!isFormValid()}
            >
              GUARDAR CONTACTO
            </Button>
          ) : (
            <>
              <Button 
                type="button" 
                variant="primary" 
                onClick={handleSaveContact}
                disabled={!isFormValid()}
              >
                EDITAR CONTACTO
              </Button>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleCancelEdit}
              >
                CANCELAR
              </Button>
            </>
          )}
        </div>

        <div className={styles.searchSection}>
          <Input
            ref={searchInputRef}
            name="search"
            value={searchValue}
            onChange={handleSearchInputChange}
            placeholder="Buscar por nombre o teléfono..."
            isSearch={true}
            disabled={isLoading || isSearching}
            style={{ width: "100%" }}
          />
        </div>

        <div className={styles.tableSection}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <p>Cargando contactos...</p>
            </div>
          ) : (
            <Table 
              headers={headers} 
              data={contacts}
              onPress={handleContactRowClick}
              renderActions={renderActions}
              emptyMessage="No se encontraron contactos"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactsModal;